import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Button,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { motion, PanInfo } from 'framer-motion';
import { FiX } from 'react-icons/fi';

const MotionModalContent = motion(ModalContent);

const DRAG_DISMISS_THRESHOLD = 150;

interface BottomSheetContentProps {
  onClose: () => void;
  maxH?: string;
  children: React.ReactNode;
}

/**
 * The draggable bottom-sheet panel (drag handle + close button + content).
 * Use this directly when the parent already renders a Chakra <Modal>/overlay
 * (e.g. a multi-step modal that swaps its content). Otherwise use <BottomSheet>.
 */
export const BottomSheetContent: React.FC<BottomSheetContentProps> = ({
  onClose,
  maxH = '90vh',
  children,
}) => {
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y > DRAG_DISMISS_THRESHOLD) onClose();
  };

  return (
    <MotionModalContent
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      mb={0}
      ml={0}
      mr={0}
      width="100vw"
      maxWidth="100vw"
      borderRadius="28px 28px 0 0"
      height="auto"
      maxH={maxH}
      overflow="hidden"
      bg="#FFFFFF"
      color="#0E0E0E"
      fontFamily="display"
      boxShadow="0px -4px 24px 0px rgba(14,14,14,0.08)"
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.5 }}
      onDragEnd={handleDragEnd}
    >
      {/* Drag Handle */}
      <Flex justify="center" pt="10px" pb="4px" cursor="grab" _active={{ cursor: 'grabbing' }}>
        <Box aria-hidden="true" w="40px" h="4px" bg="#DCDCD8" borderRadius="999px" />
      </Flex>

      {/* Close Button */}
      <Flex justify="flex-end" px="20px">
        <Button
          aria-label="Close"
          onClick={onClose}
          minW="44px"
          h="44px"
          w="44px"
          p={0}
          borderRadius="999px"
          bg="#F2F2F0"
          color="#0E0E0E"
          _hover={{ bg: '#E4E4E0' }}
          _active={{ bg: '#E4E4E0' }}
        >
          <Icon as={FiX} boxSize="18px" strokeWidth={2.4} />
        </Button>
      </Flex>

      {children}
    </MotionModalContent>
  );
};

interface BottomSheetProps extends BottomSheetContentProps {
  isOpen: boolean;
}

/**
 * Full bottom-sheet modal: Chakra <Modal> + overlay + draggable panel.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({ isOpen, onClose, maxH, children }) => (
  <Modal isOpen={isOpen} onClose={onClose} motionPreset="none">
    <ModalOverlay bg="rgba(14,14,14,0.55)" />
    <BottomSheetContent onClose={onClose} maxH={maxH}>
      {children}
    </BottomSheetContent>
  </Modal>
);

export default BottomSheet;
